/**
 * Carries a `?next=` return path across the auth pages.
 *
 * An action button sends an anonymous visitor to /sign-in?next=<detail page>,
 * and SignInPage navigates back there afterwards. But a visitor who does not
 * have an account yet goes on to sign UP, and every hop between the two auth
 * pages used to drop the parameter:
 *
 *   SignUpPage  -> navigate('/sign-in')   after registering
 *   SignUpForm  -> navigate('/sign-in')   "already have an account"
 *   SignInForm  -> navigate('/sign-up')   "create an account"
 *
 * So "sign up, then continue what you were doing" landed the visitor on the
 * dashboard with no sign of the thing they had clicked. Sign IN worked, which
 * is why this went unnoticed: the broken path is the one a NEW visitor takes,
 * and a developer testing it is almost never new.
 *
 * THE GUARD IS THE SAME ONE SignInPage ALREADY APPLIES, restated here rather
 * than imported so that no page can carry a value another page would refuse: a
 * return path must be a single-slash relative path. '//evil.example' is a
 * protocol-relative URL and would leave the site, so it is dropped rather than
 * sanitised — a half-trusted redirect target is not worth keeping.
 */

/** The safe `next` in a query string, or '' when there is none. */
export function readNext(search) {
  try {
    const next = new URLSearchParams(search || '').get('next');
    if (!next) return '';
    if (!next.startsWith('/') || next.startsWith('//')) return '';
    return next;
  } catch (e) {
    return '';
  }
}

/**
 * Appends the current page's `next` to an auth path, when there is one.
 *
 * `withNext('/sign-in')` from a page opened as `?next=/p/event/1` gives
 * `/sign-in?next=%2Fp%2Fevent%2F1`, and plain `/sign-in` otherwise — so a
 * visitor who arrived without a return path is not given one.
 */
export function withNext(path, search) {
  const next = readNext(search === undefined ? window.location.search : search);
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

/*
 * ── Carrying `next` across the SSO round trip ────────────────────────────────
 *
 * `?next=` rides the auth pages in the URL, which is enough for password
 * sign-in: SignInPage reads it back off its own query string. "Continue with
 * Google" leaves the SPA entirely — browser -> backend authorize -> the IdP ->
 * back to /auth/sso/callback/<provider>?state&code — and that return URL is
 * allowlisted server-side (SsoProviderConfig.redirect_uri_allowlist), so the
 * parameter cannot simply be appended to it: an unlisted redirect_uri is a 400
 * from the authorize view. The value therefore has to wait somewhere on the
 * client while the trip happens.
 *
 * sessionStorage is that place. It is per-tab, survives the full-page
 * navigations the round trip is made of (including Apple's form_post bounce,
 * which 302s back into the same tab), and holds nothing but a path the visitor
 * was already looking at.
 *
 * It is read ONCE and expires, because a stash outlives the trip it was made
 * for: click Google from an event page, abandon it, sign in from the header an
 * hour later, and a kept stash would drop the visitor on that old event. Ten
 * minutes is longer than any authorize round trip and shorter than a change of
 * mind.
 */

const SSO_NEXT_KEY = 'arcee_sso_next';
const SSO_NEXT_TTL_MS = 10 * 60 * 1000;

/**
 * Remembers the current page's `next` for the SSO round trip, if there is one.
 *
 * Called from every "continue with <provider>" click. A visitor who arrived
 * without a return path stashes nothing AND clears any older stash, so the
 * previous trip's destination can never be inherited by this one.
 */
export function stashSsoNext(search) {
  try {
    const next = readNext(search === undefined ? window.location.search : search);
    if (!next) {
      sessionStorage.removeItem(SSO_NEXT_KEY);
      return '';
    }
    sessionStorage.setItem(SSO_NEXT_KEY, JSON.stringify({ next, at: Date.now() }));
    return next;
  } catch (e) {
    // Private mode / storage disabled: SSO still signs the visitor in, they
    // just land on the default page as they did before.
    return '';
  }
}

/**
 * Takes back the stashed `next`, clearing it, or '' when there is none.
 *
 * Re-validated on the way out as well as in: the guard is cheap and the value
 * has been sitting in storage the visitor's own devtools can reach.
 */
export function takeSsoNext() {
  try {
    const raw = sessionStorage.getItem(SSO_NEXT_KEY);
    if (!raw) return '';
    sessionStorage.removeItem(SSO_NEXT_KEY);
    const parsed = JSON.parse(raw);
    const next = parsed && typeof parsed.next === 'string' ? parsed.next : '';
    if (!next || !next.startsWith('/') || next.startsWith('//')) return '';
    if (!parsed.at || Date.now() - parsed.at > SSO_NEXT_TTL_MS) return '';
    return next;
  } catch (e) {
    return '';
  }
}
